'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

interface NavItem {
  label: string;
  href?: string;
  children?: { label: string; href: string }[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Flights',
    children: [
      { label: 'Flight Search', href: '/flights' },
      { label: 'Pre-Purchased', href: '/pre-purchased-flights' },
      { label: 'FD Flights', href: '/fd-flights' },
    ],
  },
  {
    label: 'Hotels',
    href: '/hotels',
  },
  {
    label: 'Voyages',
    children: [
      { label: 'Luxury Cruises', href: '/cruises' },
      { label: 'Worldway × Crystal Cruises', href: '/crystal-cruises' },
      { label: 'Crystal — Voyage Search', href: '/crystal-cruises/search' },
      { label: 'Crystal — Collections', href: '/crystal-cruises/collections' },
      { label: 'Crystal — Voyage Map', href: '/crystal-cruises/map' },
      { label: 'Crystal — AI Concierge', href: '/crystal-cruises/concierge' },
      { label: 'Expedition Cruises', href: '/expedition-cruises' },
      { label: 'River Cruises', href: '/river-cruises' },
      { label: 'World Cruises', href: '/world-cruises' },
      { label: 'Luxury Rail', href: '/rail' },
      { label: 'Yacht Charters', href: '/yachts' },
    ],
  },
  {
    label: 'Journeys',
    children: [
      { label: 'All Journeys', href: '/all-journeys' },
      { label: 'Small Group', href: '/small-group' },
      { label: 'Tailor-Made', href: '/tailor-made' },
      { label: 'Safari', href: '/safari' },
      { label: 'Polar Expeditions', href: '/polar-expeditions' },
      { label: 'Cultural & Heritage', href: '/cultural' },
      { label: 'Honeymoon', href: '/honeymoon' },
      { label: 'Wellness', href: '/wellness' },
      { label: 'Family', href: '/family' },
      { label: 'Villas & Estates', href: '/villas' },
    ],
  },
  {
    label: 'Experiences',
    children: [
      { label: 'Tours', href: '/tours' },
      { label: 'Browse Collections', href: '/tours/browse' },
      { label: 'Activities', href: '/activities' },
      { label: 'Merchant', href: '/experiences/merchant' },
      { label: 'Integration Report', href: '/viator-integration-sync' },
      { label: 'Transfers', href: '/transfers' },
      { label: 'Coach & Bus', href: '/buses' },
      { label: 'Trip Builder', href: '/trip-builder' },
      { label: 'Travel Insurance', href: '/insurance' },
      { label: 'Visa Services', href: '/visa' },
    ],
  },
  {
    label: 'Private Aviation',
    children: [
      { label: 'Private Jets', href: '/private-jets' },
      { label: 'Empty Legs', href: '/private-aviation/empty-legs' },
    ],
  },
  {
    label: 'Concierge',
    href: '/ai-concierge',
  },
  {
    label: 'Membership',
    href: '/membership',
  },
  {
    label: 'Support',
    href: '/help-support',
  },
];

function DropdownMenu({ items, isOpen }: { items: { label: string; href: string }[]; isOpen: boolean }) {
  if (!isOpen) return null;
  return (
    <div
      role="menu"
      className="absolute left-1/2 top-full z-[200] mt-1 min-w-[210px] -translate-x-1/2 rounded-md border border-[var(--border)] bg-[var(--surface-2)] py-2 shadow-2xl"
      style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
    >
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          role="menuitem"
          className="block px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[var(--muted-foreground)] transition-colors hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]"
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}

function NavDropdown({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref} style={{ overflow: 'visible' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group relative inline-flex items-center gap-1 whitespace-nowrap px-3 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--muted-foreground)] transition-colors duration-300 hover:text-[var(--primary)] after:pointer-events-none after:absolute after:bottom-0 after:left-3 after:right-3 after:h-px after:origin-center after:scale-x-0 after:bg-gradient-to-r after:from-transparent after:via-[var(--primary)] after:to-transparent after:opacity-0 after:transition-all after:duration-500 hover:after:scale-x-100 hover:after:opacity-100"
      >
        {item.label}
        <svg
          width="7"
          height="7"
          viewBox="0 0 10 10"
          aria-hidden="true"
          className={`opacity-70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
        </svg>
      </button>
      <DropdownMenu items={item.children!} isOpen={open} />
    </div>
  );
}

export default function WorldwayNav() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={`sticky top-0 z-40 border-b transition-all duration-300 ${
          scrolled
            ? 'border-[var(--border)]/60 bg-[var(--background)]/95 backdrop-blur-xl'
            : 'border-[var(--border)]/40 bg-[var(--background)]/80 backdrop-blur-xl'
        }`}
        style={{ overflow: 'visible' }}
      >
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-3" style={{ overflow: 'visible' }}>
          {/* Logo */}
          <Link
            href="/"
            aria-label="WorldwayLuxe home"
            className="mr-auto flex shrink-0 items-center gap-3 lg:mr-8"
          >
            <AppLogo size={40} />
            <span className="flex flex-col leading-tight whitespace-nowrap">
              <span className="font-serif text-[0.95rem] tracking-[0.24em] text-[var(--primary)]">WORLDWAY</span>
              <span className="text-[0.55rem] uppercase tracking-[0.44em] text-[var(--muted-foreground)]">Luxe</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 lg:flex" style={{ overflow: 'visible' }}>
            {NAV_ITEMS.map((item) => {
              if (item.children) {
                return <NavDropdown key={item.label} item={item} />;
              }
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.label}
                  href={item.href!}
                  className={`group relative whitespace-nowrap px-3 py-2 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 after:pointer-events-none after:absolute after:bottom-0 after:left-3 after:right-3 after:h-px after:origin-center after:scale-x-0 after:bg-gradient-to-r after:from-transparent after:via-[var(--primary)] after:to-transparent after:opacity-0 after:transition-all after:duration-500 hover:after:scale-x-100 hover:after:opacity-100 ${
                    isActive
                      ? 'text-[var(--primary)] after:scale-x-100 after:opacity-100'
                      : 'text-[var(--muted-foreground)] hover:text-[var(--primary)]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Search icon */}
            <Link
              href="/booking"
              aria-label="Search"
              className="hidden lg:flex items-center justify-center w-8 h-8 rounded-full text-[var(--muted-foreground)] hover:text-[var(--primary)] transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </Link>

            {/* Sign in */}
            <Link
              href="/auth/login"
              className="hidden lg:flex items-center gap-2 px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--muted-foreground)] border border-[var(--border)] rounded-full hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all duration-200"
            >
              Sign in
            </Link>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--surface-4)] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute top-[65px] left-0 right-0 bg-[var(--surface-2)] border-b border-[var(--border)] max-h-[calc(100vh-65px)] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col py-2">
              {NAV_ITEMS.map((item) => {
                if (item.children) {
                  const isExpanded = mobileExpanded === item.label;
                  return (
                    <div key={item.label}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--muted-foreground)] hover:text-[var(--primary)] hover:bg-[var(--surface-4)] transition-colors"
                        onClick={() => setMobileExpanded(isExpanded ? null : item.label)}
                      >
                        {item.label}
                        <svg
                          width="7"
                          height="7"
                          viewBox="0 0 10 10"
                          aria-hidden="true"
                          className={`opacity-70 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        >
                          <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                      </button>
                      {isExpanded && (
                        <div className="bg-[var(--surface-1)] border-t border-[var(--border)]/50">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              href={child.href}
                              onClick={() => setMobileOpen(false)}
                              className="block px-10 py-2.5 text-[11px] uppercase tracking-[0.18em] text-[var(--muted-foreground)] hover:text-[var(--primary)] hover:bg-[var(--primary)]/5 transition-colors"
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    href={item.href!}
                    onClick={() => setMobileOpen(false)}
                    className={`px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors hover:bg-[var(--surface-4)] ${
                      pathname === item.href
                        ? 'text-[var(--primary)]'
                        : 'text-[var(--muted-foreground)] hover:text-[var(--primary)]'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="px-6 py-4 border-t border-[var(--border)]/50 mt-2">
                <Link
                  href="/auth/login"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center px-4 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--muted-foreground)] border border-[var(--border)] rounded-full hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all"
                >
                  Sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
